import type { V2AdminNegotiationDetailDto } from '@idc-forms/api-client'
import { NegotiationsPermission } from '@idc-forms/permissions'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Edit2,
  ExternalLink,
  Eye,
  EyeOff,
  Landmark,
  Link2,
  Send,
  SquareX,
  Undo2
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { ROUTE_ACTIONS } from '@/app/router/paths'
import { PATHS } from '@/app/router/routes'
import useAuth from '@/features/auth/hooks/use-auth'
import { useStatesLookupQuery } from '@/features/states/hooks/use-states-query'
import { EnumTypeStates } from '@/features/states/models/state.schema'
import {
  type SidePanelAction,
  SidePanelActions
} from '@/shared/components/side-panel/side-panel-actions'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useStatePermissions } from '@/shared/hooks/use-state-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { extractApiError, handleMutationError } from '@/shared/utils/handle-api-error'
import { ROUTES_NEGOTIATIONS } from '../../consts/routes'
import { useReleaseAccessAfterApproval } from '../../hooks/use-release-access-after-approval'
import { invalidateNegotiationAfterTransition } from '../../operations/invalidate-after-transition'
import { missingAttachmentsFromError } from '../../operations/missing-attachments-from-error'
import {
  NegotiationAction,
  resolveNegotiationActions
} from '../../operations/resolve-negotiation-actions'
import { negotiationsAdminV2Service } from '../../services/negotiations-admin-v2.service'
import DialogFormChangeState, { type IFormChangeState } from '../dialog-form-change-state'
import { MissingAttachmentsDialog } from '../missing-attachments-dialog'

type MutationAction = 'advance' | 'reject' | 'inactive' | 'activate' | 'rescind'

const SUCCESS_MESSAGE: Record<MutationAction, string> = {
  advance: 'Negociación enviada al siguiente estado',
  reject: 'Negociación regresada al estado anterior',
  inactive: 'Negociación inactivada',
  activate: 'Negociación activada',
  rescind: 'Negociación rescindida'
}

const LOADING_MESSAGE: Record<MutationAction, string> = {
  advance: 'Cambiando estado de la negociación…',
  reject: 'Regresando la negociación…',
  inactive: 'Inactivando negociación…',
  activate: 'Activando negociación…',
  rescind: 'Rescindiendo negociación…'
}

type NegotiationDetailPanelActionsProps = {
  detail: V2AdminNegotiationDetailDto
}

/**
 * El `neg_` del expediente, o revienta.
 *
 * El tipo lo declara opcional porque el backfill era una de las migraciones del
 * corte; ya corrió en los tres entornos, así que un expediente sin él sería un
 * dato imposible. Se falla RUIDOSO en vez de caer a v1: un puente que nadie
 * borra es peor que un error que no puede ocurrir.
 */
// `requirePublicId` se fue con v1: allá el `publicId` era opcional y había que exigirlo antes
// de armar una llamada v2. El detalle v2 publica `id` y es su identidad, así que no hay nada
// que verificar — el tipo ya lo garantiza.

export function NegotiationDetailPanelActions({ detail }: NegotiationDetailPanelActionsProps) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const closePanel = useSidePanelStore((state) => state.closePanel)
  const { has } = usePermissions()
  const { canActivateNegotiation, canExecuteState, canRescindApprovedNegotiation } =
    useStatePermissions()
  const { data: states = [] } = useStatesLookupQuery()
  const [activeAction, setActiveAction] = useState<MutationAction | null>(null)
  const [confirmRescind, setConfirmRescind] = useState(false)
  const [missingAttachments, setMissingAttachments] = useState<
    Array<{ id: string; name: string; description?: string }>
  >([])
  const [showMissingDialog, setShowMissingDialog] = useState(false)
  const releaseAccessAfterApproval = useReleaseAccessAfterApproval(detail.id)

  const currentState = detail.currentState?.state ?? null
  const currentType = currentState?.type
  // Acá vivían `isInactive`, `isApproved`, `isRescinded` y `isTerminal`. Los cuatro existían
  // ÚNICAMENTE para decidir qué botones mostrar, y esa decisión se fue entera a
  // `resolveNegotiationActions`, que la recibe como `currentType` y la resuelve probada. Lo
  // descubrió el linter al quedarse sin usos — el refactor no los dejó de sobra: no eran de nadie
  // más desde el principio.

  /*
   * ☠️ ACÁ SE RECORRÍA EL FLUJO A MANO, Y ESTABA MAL.
   *
   * La versión anterior buscaba el paso siguiente con `order + 1` sobre `workflow.states`. Los
   * `order` de un flujo **tienen huecos por diseño** —borrar un estado lo saca de los flujos con
   * `$pull`, que conserva los `order` de los que quedan—, así que un expediente parado justo
   * antes de un hueco se quedaba **sin destino** y el botón «Avanzar» **desaparecía**. La API ya
   * tenía la regla correcta (el menor `order` mayor que el actual) y hoy publica la respuesta.
   *
   * Con eso se fue también la última atadura del panel al detalle v1: la lista interna de estados
   * del flujo la publica sólo v1, y era lo único que quedaba pidiéndola.
   *
   * Lo que sigue saliendo del catálogo son INACTIVO y RESCINDIDO, que no dependen del flujo: son
   * estados sembrados por tipo. Su permiso se comprueba contra la matriz local porque está
   * indexada por el id interno, que es justamente el que el catálogo v1 publica.
   */
  const transition = useMemo(
    () => ({
      inactiveState: states.find((state) => state.type === EnumTypeStates.inactive) ?? null,
      rescindState: states.find((state) => state.type === EnumTypeStates.rescind) ?? null,
      // Avanzar CIERRA la venta cuando el destino es el estado terminal — la misma condición que
      // usa el botón del expediente. Hasta el 2026-08-05 el panel no la miraba, así que cerraba
      // ventas sin liberarle al comprador el acceso a su inmueble en la app.
      closesTheSale: detail.transitions.next?.type === EnumTypeStates.end
    }),
    [detail.transitions.next?.type, states]
  )

  const mutation = useMutation({
    mutationFn: async ({
      action,
      values
    }: {
      action: MutationAction
      values: IFormChangeState
    }) => {
      const toastId = toast.loading(LOADING_MESSAGE[action])
      try {
        const result = await (async () => {
          switch (action) {
            case 'advance':
              return await negotiationsAdminV2Service.advance(
                accessToken,
                detail.id,
                values.comment
              )
            case 'reject':
              return await negotiationsAdminV2Service.reject(accessToken, detail.id, values.comment)
            // LAS CINCO viajan por v2 y se direccionan por `neg_`. Ya no queda
            // ninguna identidad mezclada en este switch.
            case 'inactive':
              return await negotiationsAdminV2Service.inactivate(
                accessToken,
                detail.id,
                values.comment
              )
            case 'activate':
              return await negotiationsAdminV2Service.reactivate(
                accessToken,
                detail.id,
                values.comment
              )
            case 'rescind':
              return await negotiationsAdminV2Service.rescind(
                accessToken,
                detail.id,
                values.comment
              )
          }
        })()
        toast.success(SUCCESS_MESSAGE[action], { id: toastId })
        return result
      } catch (error) {
        toast.dismiss(toastId)
        throw error
      }
    },
    onSuccess: async (_result, variables) => {
      setActiveAction(null)
      // Por la operación compartida y no a mano: la política de qué refrescar después de una
      // transición vive en un solo lado a propósito (ver su comentario). Esta llamada hacía
      // exactamente lo mismo, que es justo lo que la vuelve peligrosa — el día que la política
      // cambie, la copia se queda vieja sin que nada falle.
      invalidateNegotiationAfterTransition(queryClient)

      // DESPUÉS de que la venta cerró, y sólo si se pidió: liberar el acceso del comprador a su
      // inmueble en la app. Va acá y no dentro de la mutación por lo mismo que en el expediente —
      // un fallo del correo no puede tumbar una venta ya cerrada.
      if (variables.action === 'advance' && variables.values.releaseMobileAccess) {
        void releaseAccessAfterApproval()
      }
    },
    onError: (error, variables) => {
      // Comparar por CÓDIGO y no por mensaje estaba bien; los NOMBRES de los campos no. Esta
      // rama leía `data.errorCode` y `data.details`, que es el sobre de v1: v2 responde Problem
      // Details y manda `code` y `errors`. `missingAttachmentsFromError` usa el normalizador que
      // entiende las tres formas, en vez de una cuarta lectura a mano.
      const faltantes = variables.action === 'advance' ? missingAttachmentsFromError(error) : null
      if (faltantes) {
        setMissingAttachments(faltantes)
        setShowMissingDialog(true)
        return
      }
      // La UI compara CÓDIGOS, nunca mensajes: el texto es traducción, el código
      // es contrato. Reactivar puede chocar con un inmueble que otra negociación
      // tomó mientras ésta estaba apagada — no es culpa de quien reactiva, así
      // que se cuenta con el código de la que bloquea en vez de un error genérico.
      // ☠️ Esta rama leía `data.errorCode` y `data.details`, que es el sobre de v1: v2 responde
      // Problem Details y manda `code` y `errors`, así que NUNCA entraba — reactivar contra un
      // inmueble tomado caía al toast genérico y no decía cuál negociación lo bloquea, que es
      // justo el dato por el que existe. `extractApiError` traduce las tres formas.
      const apiError = extractApiError(error)
      if (
        variables.action === 'activate' &&
        apiError?.errorCode === 'PROPERTY_HAS_ACTIVE_NEGOTIATION'
      ) {
        const blocking = (apiError.details as { negotiationCode?: unknown } | undefined)
          ?.negotiationCode
        toast.error(
          blocking
            ? `El inmueble ya lo está trabajando la negociación ${blocking}. No se puede reactivar esta.`
            : 'El inmueble ya lo está trabajando otra negociación. No se puede reactivar esta.'
        )
        return
      }
      handleMutationError(error)
    }
  })

  const openNegotiation = () => {
    closePanel()
    navigate(PATHS.admin.negotiations.view(detail.id))
  }

  const editNegotiation = () => {
    closePanel()
    navigate(PATHS.admin.negotiations.edit(detail.id))
  }

  const openCredit = () => {
    closePanel()
    navigate(PATHS.admin.credits.fromNegotiation(detail.id, detail.code))
  }

  const copyNegotiationLink = async () => {
    try {
      const path = PATHS.admin.negotiations.view(detail.id)
      await navigator.clipboard.writeText(new URL(path, window.location.origin).toString())
      toast.success('Enlace de la negociación copiado', { duration: 1500 })
    } catch {
      toast.error('No se pudo copiar el enlace')
    }
  }

  /*
   * QUÉ se ofrece lo decide `resolveNegotiationActions`, que es puro y está probado con 20 casos
   * —incluidos los permisos saboteados— porque el runner de la web corre en `node` y no monta
   * componentes. Acá queda sólo CÓMO se ve cada acción: etiqueta, ícono y handler.
   */
  const permitidas = new Set(
    resolveNegotiationActions({
      currentType,
      canAdvance: detail.transitions.canAdvance,
      canReject: detail.transitions.canReject,
      inactiveStateId: transition.inactiveState?._id,
      rescindStateId: transition.rescindState?._id,
      can: {
        updateNegotiation: has(NegotiationsPermission.UpdateNegotiation),
        activateNegotiation: canActivateNegotiation(),
        rescindApproved: canRescindApprovedNegotiation(),
        executeState: canExecuteState
      }
    })
  )

  const targetIsEnd = detail.transitions.next?.type === EnumTypeStates.end
  const presentacion: Record<NegotiationAction, SidePanelAction> = {
    [NegotiationAction.Open]: {
      label: 'Abrir negociación',
      icon: <ExternalLink aria-hidden="true" className="h-4 w-4" />,
      onClick: openNegotiation,
      primary: true
    },
    [NegotiationAction.Edit]: {
      label: 'Editar negociación',
      icon: <Edit2 aria-hidden="true" className="h-4 w-4" />,
      onClick: editNegotiation,
      variant: 'secondary'
    },
    [NegotiationAction.CopyLink]: {
      label: 'Copiar enlace',
      icon: <Link2 aria-hidden="true" className="h-4 w-4" />,
      onClick: () => {
        void copyNegotiationLink()
      },
      variant: 'ghost'
    },
    [NegotiationAction.Advance]: {
      label: targetIsEnd
        ? 'Autorizar negociación'
        : `Enviar a ${detail.transitions.next?.name ?? 'siguiente estado'}`,
      icon: <Send aria-hidden="true" className="h-4 w-4" />,
      onClick: () => {
        setActiveAction('advance')
      },
      disabled: mutation.isPending
    },
    [NegotiationAction.Reject]: {
      label: 'Regresar al estado anterior',
      icon: <Undo2 aria-hidden="true" className="h-4 w-4" />,
      onClick: () => {
        setActiveAction('reject')
      },
      disabled: mutation.isPending
    },
    [NegotiationAction.Activate]: {
      label: 'Activar negociación',
      icon: <Eye aria-hidden="true" className="h-4 w-4" />,
      onClick: () => {
        setActiveAction('activate')
      },
      disabled: mutation.isPending
    },
    [NegotiationAction.Inactivate]: {
      label: 'Inactivar negociación',
      icon: <EyeOff aria-hidden="true" className="h-4 w-4" />,
      onClick: () => {
        setActiveAction('inactive')
      },
      disabled: mutation.isPending
    },
    [NegotiationAction.Rescind]: {
      label: 'Rescindir negociación',
      icon: <SquareX aria-hidden="true" className="h-4 w-4" />,
      onClick: () => {
        setConfirmRescind(true)
      },
      variant: 'destructive',
      disabled: mutation.isPending
    },
    [NegotiationAction.Credit]: {
      label: 'Abrir crédito',
      icon: <Landmark aria-hidden="true" className="h-4 w-4" />,
      onClick: openCredit
    }
  }

  // El ORDEN sale del catálogo, no del orden en que se declara la presentación: es el mismo en el
  // que `resolveNegotiationActions` las devuelve, y así los dos archivos no pueden discrepar.
  const actions: SidePanelAction[] = Object.values(NegotiationAction)
    .filter((accion) => permitidas.has(accion))
    .map((accion) => presentacion[accion])

  const dialogTitle: Record<MutationAction, string> = {
    advance:
      detail.transitions.next?.type === EnumTypeStates.end
        ? 'Autorizar negociación'
        : `Enviar a ${detail.transitions.next?.name ?? 'siguiente estado'}`,
    reject: 'Regresar al estado anterior',
    inactive: 'Inactivar negociación',
    activate: 'Activar negociación',
    rescind: 'Rescindir negociación'
  }

  const handleSubmit = (values: IFormChangeState) => {
    if (!activeAction) return
    mutation.mutate({ action: activeAction, values })
  }

  const handleGoToAttachments = () => {
    setShowMissingDialog(false)
    closePanel()
    navigate(
      PATHS.admin.negotiations.step(ROUTE_ACTIONS.view, detail.id, ROUTES_NEGOTIATIONS.ATTACHMENTS)
    )
  }

  return (
    <>
      <SidePanelActions actions={actions} />

      <DialogFormChangeState
        open={activeAction !== null}
        onOpenChange={(open) => {
          if (!open) setActiveAction(null)
        }}
        onSubmit={handleSubmit}
        title={activeAction ? dialogTitle[activeAction] : 'Actualizar negociación'}
        textButtonSend={activeAction === 'rescind' ? 'Rescindir' : 'Confirmar'}
        showSapToggle={import.meta.env.DEV && activeAction === 'advance' && !!currentState?.sendSAP}
        showMobileAccessToggle={activeAction === 'advance' && transition.closesTheSale && true}
      />

      <AlertDialog open={confirmRescind} onOpenChange={setConfirmRescind}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rescindir esta negociación?</AlertDialogTitle>
            <AlertDialogDescription>
              La negociación dejará de aplicar al inmueble actual. Podrás registrar el motivo en el
              siguiente paso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRescind(false)
                setActiveAction('rescind')
              }}
            >
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MissingAttachmentsDialog
        open={showMissingDialog}
        onClose={() => {
          setShowMissingDialog(false)
        }}
        missingAttachments={missingAttachments}
        onGoToAttachments={handleGoToAttachments}
      />
    </>
  )
}
